import { useLayoutEffect, useRef, type RefObject } from "react";

/**
 * Marks an element the restore may anchor on, its value an id stable across renders: the
 * content search's workspace cards carry it.
 */
export const SCROLL_ANCHOR_ATTRIBUTE = "data-scroll-anchor" as const;

/**
 * Where the reader was in one view: the raw offset, and the first anchor on screen with
 * its distance from the top of the view. The anchor keeps the reader on the same card
 * when the content above it is not the same height on return: back on the page, the
 * workspace table above the cards fills in after the cards, which are served from cache.
 */
interface ScrollPosition {
  offset: number;
  anchor?: { id: string; top: number };
}

/**
 * Positions per view of the Workspaces search: a term, a result type and a zoom make one
 * view. Kept in memory for the life of the app rather than in storage: a reload also
 * drops the pages "Vis flere workspaces" loaded, so a position kept across it would point
 * past the end of the list.
 */
const positions = new Map<string, ScrollPosition>();
const MAX_REMEMBERED_VIEWS = 50;

const remember = (viewKey: string, position: ScrollPosition) => {
  positions.delete(viewKey);
  positions.set(viewKey, position);
  if (positions.size > MAX_REMEMBERED_VIEWS) {
    const oldest = positions.keys().next().value;
    if (oldest !== undefined) positions.delete(oldest);
  }
};

const anchorsIn = (container: HTMLElement) =>
  Array.from(
    container.querySelectorAll<HTMLElement>(`[${SCROLL_ANCHOR_ATTRIBUTE}]`)
  );

const readPosition = (container: HTMLElement): ScrollPosition => {
  const viewTop = container.getBoundingClientRect().top;
  const onScreen = anchorsIn(container).find(
    (anchor) => anchor.getBoundingClientRect().bottom > viewTop
  );
  const id = onScreen?.getAttribute(SCROLL_ANCHOR_ATTRIBUTE);
  if (!onScreen || !id) return { offset: container.scrollTop };
  return {
    offset: container.scrollTop,
    anchor: { id, top: onScreen.getBoundingClientRect().top - viewTop },
  };
};

/** The offset that puts the remembered anchor back where it was, or the raw offset. */
const offsetFor = (
  container: HTMLElement,
  position: ScrollPosition
): number => {
  const { anchor } = position;
  const element =
    anchor &&
    anchorsIn(container).find(
      (candidate) =>
        candidate.getAttribute(SCROLL_ANCHOR_ATTRIBUTE) === anchor.id
    );
  if (!anchor || !element) return position.offset;
  const viewTop = container.getBoundingClientRect().top;
  return (
    container.scrollTop +
    element.getBoundingClientRect().top -
    viewTop -
    anchor.top
  );
};

/** How long a restore keeps waiting for the view's content to grow tall enough. */
const RESTORE_WINDOW_MS = 3000;

/**
 * How often a restore checks again. Timers rather than animation frames: a tab that is
 * not visible runs no frames, and the restore must not depend on it.
 */
const RESTORE_RETRY_MS = 50;

/** Input that means the reader is scrolling, so a restore in progress must stop. */
const TAKEOVER_EVENTS = [
  "wheel",
  "touchstart",
  "keydown",
  "mousedown",
] as const;

/**
 * Brings a view of the Workspaces search back to where the reader left it (LAR-235):
 * after zooming into a workspace and back, switching result type, or returning from a
 * workspace or document opened from the results. A view seen for the first time starts
 * at the top.
 *
 * The content may not be tall enough yet when the view appears, its data still on the
 * way, so the position is applied as far as the content allows and again as it grows,
 * until it is reached, the reader scrolls, or the window runs out. Once the anchor is
 * back in place, the browser's own scroll anchoring holds it there while content above
 * it fills in. The restore's own scrolling is not recorded; the reader's is, on every
 * scroll event (the browser sends at most one per frame).
 */
export const useSearchScrollRestoration = (
  containerRef: RefObject<HTMLElement | null>,
  /** Identifies the view on screen; null turns restoration off (no search). */
  viewKey: string | null
): void => {
  const restoringRef = useRef(false);

  // A layout effect, so a view whose content is already there never flashes at the top.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (viewKey === null || !container) return;

    const position = positions.get(viewKey) ?? { offset: 0 };
    const until = Date.now() + RESTORE_WINDOW_MS;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const restore = () => {
      if (!restoringRef.current) return;
      const target = Math.max(0, offsetFor(container, position));
      const reachable = Math.max(
        0,
        container.scrollHeight - container.clientHeight
      );
      container.scrollTop = Math.min(target, reachable);
      if (reachable >= target || Date.now() >= until) {
        restoringRef.current = false;
        return;
      }
      retry = setTimeout(restore, RESTORE_RETRY_MS);
    };

    const takeOver = () => {
      restoringRef.current = false;
    };

    const record = () => {
      if (restoringRef.current) return;
      remember(viewKey, readPosition(container));
    };

    restoringRef.current = true;
    restore();
    TAKEOVER_EVENTS.forEach((type) =>
      container.addEventListener(type, takeOver, { passive: true })
    );
    container.addEventListener("scroll", record, { passive: true });

    return () => {
      restoringRef.current = false;
      clearTimeout(retry);
      TAKEOVER_EVENTS.forEach((type) =>
        container.removeEventListener(type, takeOver)
      );
      container.removeEventListener("scroll", record);
    };
  }, [containerRef, viewKey]);
};
